<template>
  <div>
    <!-- Accounts Modal -->
    <main-dialog :visible="showAccounts" @hide="$emit('update:show-accounts', false)" :title="$t('inbox.emailAccounts')" hide-footer size="md">
      <div class="accounts-modal">
        <div v-if="loadingAccounts" class="text-center py-4"><b-spinner /></div>
        <div v-else-if="accounts.length === 0" class="text-center py-4 text-muted">
          <b-icon icon="envelope" font-scale="2" class="mb-2" /><p class="mb-0">{{ $t('inbox.noAccounts') }}</p>
        </div>
        <div v-else class="accounts-list">
          <div v-for="acc in accounts" :key="acc.id" class="account-card" :class="{ 'account-card--active': selectedAccountId === acc.id }">
            <div class="account-card__avatar">{{ (acc.email || acc.name || '?')[0].toUpperCase() }}</div>
            <div class="account-card__body">
              <div class="account-card__email">{{ acc.email }}</div>
              <div v-if="acc.is_owner && editingLabelId === acc.id" class="account-card__name-edit">
                <b-form-input
                  v-model="editingLabelValue"
                  size="sm"
                  autofocus
                  @keyup.enter="saveLabel(acc)"
                  @keyup.esc="editingLabelId = null"
                  @blur="saveLabel(acc)"
                  :placeholder="$t('inbox.accountLabel')"
                />
              </div>
              <div v-else-if="acc.is_owner" class="account-card__name account-card__name--editable" @click="startEditLabel(acc)">
                {{ acc.name || $t('inbox.accountLabel') }}
                <b-icon icon="pencil" class="account-card__edit-icon" />
              </div>
              <div v-else class="account-card__name">{{ acc.name }}</div>
            </div>
            <div class="account-card__status">
              <b-badge pill :variant="acc.is_confirmed ? 'success' : 'warning'">{{ acc.is_confirmed ? $t('inbox.active') : $t('inbox.unverified') }}</b-badge>
            </div>
            <div class="account-card__actions">
              <b-button v-if="acc.is_owner" size="sm" variant="light" class="mr-1" @click="$emit('manage-policies', acc)" v-b-tooltip.hover :title="$t('inbox.managePolicies')"><b-icon icon="shield-lock" /></b-button>
              <b-button v-if="acc.is_owner" size="sm" variant="outline-danger" @click="$emit('delete-account', acc)" v-b-tooltip.hover :title="$t('inbox.removeAccount')"><b-icon icon="trash" /></b-button>
            </div>
          </div>
        </div>
        <div class="accounts-modal__footer">
          <b-button variant="primary" block @click="$emit('add-account')"><b-icon icon="plus-circle" class="mr-1" /> {{ $t('inbox.connectAccount') }}</b-button>
        </div>
      </div>
    </main-dialog>

    <!-- Create Folder Modal -->
    <main-dialog id="inbox-create-folder-modal" :title="$t('inbox.createFolder')" @ok="$emit('create-folder', folderName)" :ok-disabled="!folderName.trim()" :ok-title="$t('inbox.create')" size="sm">
      <b-alert variant="danger" dismissible :show="!!folderError" @dismissed="$emit('dismiss-folder-error')">{{ folderError }}</b-alert>
      <b-form-group :label="$t('inbox.folderName')">
        <b-form-input :value="folderName" @input="$emit('update:folder-name', $event)" placeholder="e.g. Work, Archive" autofocus />
      </b-form-group>
    </main-dialog>

    <!-- Add Account Modal -->
    <main-dialog id="inbox-add-account-modal" :title="$t('inbox.addAccountTitle')" @ok="(evt) => $emit('submit-account', evt)" :ok-disabled="addingAccount" :ok-title="$t('inbox.addAccount')" size="md">
      <slot name="add-account-form"></slot>
    </main-dialog>
  </div>
</template>

<script>
export default {
  name: 'InboxModals',
  props: {
    showAccounts: { type: Boolean, default: false },
    accounts: { type: Array, default: () => [] },
    selectedAccountId: { type: [String, Number], default: null },
    loadingAccounts: { type: Boolean, default: false },
    folderName: { type: String, default: '' },
    folderError: { type: String, default: null },
    addingAccount: { type: Boolean, default: false }
  },
  data() {
    return {
      editingLabelId: null,
      editingLabelValue: ''
    };
  },
  methods: {
    startEditLabel(acc) {
      this.editingLabelId = acc.id;
      this.editingLabelValue = acc.name || '';
    },
    saveLabel(acc) {
      const newName = (this.editingLabelValue || '').trim();
      if (newName && newName !== acc.name) {
        this.$emit('rename-account', { account: acc, name: newName });
      }
      this.editingLabelId = null;
    }
  }
};
</script>

<style scoped>
.accounts-modal { display: flex; flex-direction: column; }
.accounts-list { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
.account-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 6px;
  transition: border-color 0.12s;
}
.account-card--active {
  border-color: var(--primary-start, #6c80ff);
  background: rgba(102, 126, 234, 0.05);
}
.account-card__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  flex-shrink: 0;
}
.account-card__body {
  flex: 1;
  min-width: 0;
}
.account-card__email {
  font-size: 0.875rem;
  color: var(--text-primary, #e5e7eb);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account-card__name {
  font-size: 0.75rem;
  color: var(--text-muted, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account-card__name--editable {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.account-card__name--editable:hover {
  color: var(--text-primary, #e5e7eb);
}
.account-card__edit-icon {
  font-size: 0.625rem;
  opacity: 0;
  transition: opacity 0.12s;
}
.account-card__name--editable:hover .account-card__edit-icon {
  opacity: 0.6;
}
.account-card__name-edit {
  margin-top: 2px;
}
.account-card__name-edit .form-control {
  font-size: 0.75rem;
  padding: 0.15rem 0.375rem;
  height: auto;
}
.account-card__status {
  flex-shrink: 0;
}
.account-card__actions { display: flex; align-items: center; flex-shrink: 0; }
.accounts-modal__footer { padding-top: 0.75rem; border-top: 1px solid var(--border-separator, #2a2d35); }
</style>
